// src/features/medicine-inventory/utils/helpers.js
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};



// Generate a unique prescription number
export const generatePrescriptionNumber = () => {
  const prefix = 'RX';
  const timestamp = Date.now().toString().slice(-8);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${timestamp}-${random}`;
};

// src/features/medicine-inventory/utils/helpers.js





// Generate purchase order number
export const generatePurchaseOrderNumber = () => {
  const prefix = 'PO';
  const date = new Date();
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  const timestamp = Date.now().toString().slice(-4);
  
  return `${prefix}-${year}${month}${day}-${random}${timestamp}`;
};

// Alternative format with sequential numbering (if you want to track sequence)
let purchaseOrderCounter = 1;
export const generateSequentialPurchaseOrderNumber = () => {
  const prefix = 'PO';
  const date = new Date();
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const sequence = purchaseOrderCounter.toString().padStart(4, '0');
  
  // Increment counter
  purchaseOrderCounter++;
  
  return `${prefix}-${year}${month}-${sequence}`;
};

// Generate invoice number
export const generateInvoiceNumber = () => {
  const prefix = 'INV';
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${timestamp}-${random}`;
};

// Get stock status based on current stock and reorder level
export const getStockStatus = (currentStock, reorderLevel) => {
  if (currentStock <= 0) return { status: 'out_of_stock', label: 'Out of Stock', color: 'red' };
  if (currentStock <= reorderLevel) return { status: 'low_stock', label: 'Low Stock', color: 'orange' };
  if (currentStock <= reorderLevel * 2) return { status: 'medium_stock', label: 'Medium Stock', color: 'yellow' };
  return { status: 'good_stock', label: 'Good Stock', color: 'green' };
};

// Group items by a specific key
export const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

// Calculate totals for multi-medicine purchase
export const calculatePurchaseTotals = (medicines) => {
  let subtotal = 0;
  let totalDiscount = 0;
  let totalTax = 0;
  
  const medicinesWithTotals = medicines.map(item => {
    const itemSubtotal = (item.quantity || 0) * (item.unitPrice || 0);
    const discountAmount = itemSubtotal * ((item.discount || 0) / 100);
    const taxableAmount = itemSubtotal - discountAmount;
    const taxAmount = taxableAmount * ((item.taxRate || 0) / 100);
    const total = taxableAmount + taxAmount;
    
    subtotal += itemSubtotal;
    totalDiscount += discountAmount;
    totalTax += taxAmount;
    
    return {
      ...item,
      subtotal: itemSubtotal,
      discountAmount,
      taxableAmount,
      taxAmount,
      total
    };
  });
  
  return {
    medicines: medicinesWithTotals,
    summary: {
      subtotal,
      discount: totalDiscount,
      tax: totalTax,
      total: subtotal - totalDiscount + totalTax
    }
  };
};

// Format batch number with prefix
export const formatBatchNumber = (medicineName, date) => {
  const prefix = medicineName.substring(0, 3).toUpperCase();
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${year}${month}-${random}`;
};

// Validate expiry date (should be in future)
export const isValidExpiryDate = (expiryDate) => {
  if (!expiryDate) return true; // Optional field
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  return expiry > today;
};

// Get days until expiry
export const getDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Format supplier name
export const formatSupplierName = (name) => {
  if (!name) return '';
  return name.trim().toUpperCase();
};

// Calculate payment due date based on terms
export const calculateDueDate = (purchaseDate, paymentTerms) => {
  const date = new Date(purchaseDate);
  switch(paymentTerms) {
    case 'NET_30':
      date.setDate(date.getDate() + 30);
      break;
    case 'NET_15':
      date.setDate(date.getDate() + 15);
      break;
    case 'NET_7':
      date.setDate(date.getDate() + 7);
      break;
    default:
      date.setDate(date.getDate() + 30);
  }
  return date.toISOString().split('T')[0];
};

// Format payment status badge
export const getPaymentStatusBadge = (status) => {
  switch(status) {
    case 'PAID':
      return { className: 'bg-green-100 text-green-800', label: 'Paid' };
    case 'PARTIAL':
      return { className: 'bg-yellow-100 text-yellow-800', label: 'Partial Payment' };
    case 'PENDING':
      return { className: 'bg-gray-100 text-gray-800', label: 'Pending' };
    default:
      return { className: 'bg-gray-100 text-gray-800', label: status || 'Unknown' };
  }
};

// Format payment terms for display
export const formatPaymentTerms = (terms) => {
  switch(terms) {
    case 'NET_30':
      return 'Net 30 Days';
    case 'NET_15':
      return 'Net 15 Days';
    case 'NET_7':
      return 'Net 7 Days';
    case 'CASH':
      return 'Cash on Delivery';
    case 'PREPAID':
      return 'Prepaid';
    default:
      return terms || 'Net 30 Days';
  }
};